/**
 * useTeams — RBAC teams admin data (Phase 4/5). Wraps the /v1/rbac/teams API.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { config } from '../config';

export interface Team {
  id: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  isDefault: boolean;
}

export const teamKeys = {
  all: ['rbac', 'teams'] as const,
};

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function listTeams(): Promise<Team[]> {
  const res = await fetch(`${config.API_URL}/v1/rbac/teams`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to load teams (${res.status})`);
  return res.json();
}

export function useTeams() {
  return useQuery<Team[], Error>({ queryKey: teamKeys.all, queryFn: listTeams });
}

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation<Team, Error, { name: string; description?: string }>({
    mutationFn: async (body) => {
      const res = await fetch(`${config.API_URL}/v1/rbac/teams`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail?.detail || `Failed to create team (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: teamKeys.all }),
  });
}

export function useSetTeamActive() {
  const qc = useQueryClient();
  return useMutation<Team, Error, { teamId: string; isActive: boolean }>({
    mutationFn: async ({ teamId, isActive }) => {
      const res = await fetch(
        `${config.API_URL}/v1/rbac/teams/${teamId}/active`,
        {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify({ isActive }),
        },
      );
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail?.detail || `Failed to update team (${res.status})`);
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: teamKeys.all }),
  });
}
